import { getFeatureConfigs } from '~coreServer/features';
import { SPLIT_TRAFFIC_TYPES } from '~coreServer/split/split-constants';

// This featureConfigs object should store all possible features that can be
// enabled/disabled in the application.  In many cases, we'll only primarily
// use one of the methods to toggle a feature, but it's a good practice to
// allow all three methods, as it can make testing easier in the long run.
//
// For example, showRecommendationsOnPdp is something that may initially be A/B
// tested via a cookie, but during development it would be much easier to test
// with lighthouse and sitespeed if a query string could be used, and then when
// the test is complete, it would be easiest to turn on for individual brands
// via an environment variable
//
// Example featureConfig:
//
//     showRecommendationsOnPdp: {
//        // What is the default value if no vars/cookies/queryString's are
//        // specified?
//        default: false,
//        // Is this a multivariate feature config that can be values other
//        // than simply true or false?  Enabling this prevents this module
//        // from coercing values such as "true" or "1" to true and similarly
//        // for "false" and "0" to false
//        multivariate: false,
//        // What environment variable should we look for?
//        envVar: 'SS_SHOW_RECOMMENDATIONS_ON_PDP',
//        // What cookies should we look for?
//        cookies: [
//            'ss-show-recommendations-on-pdp',
//            'SS_PDP_RECS',
//        ],
//        // What queryString should we look for?
//        queryString: 'ss-show-recommendations-on-pdp',
//    }
//
const appFeatureConfigs = {
    brazeSdk: {
        default: true,
    },
    intercom: {
        default: true,
        multivariate: false,
        envVar: 'NUU_INTERCOM_ENABLED',
        cookies: [
            'nuu-intercom-enabled',
        ],
        queryString: 'nuu-intercom-enabled',
    },
    recurlyJs: {
        default: true,
        devOnly: true, // Do not allow usage in prod builds
        multivariate: false,
        envVar: 'NUU_RECURLY_JS_ENABLED',
        cookies: [
            'nuu-recurly-js-enabled',
        ],
        queryString: 'nuu-recurly-js-enabled',
    },
    // NU_FEATURE: GIFT_CARDS
    // https://urbnit.atlassian.net/browse/TYP-27405
    giftCards: {
        default: true,
        multivariate: false,
        envVar: 'NUU_ENABLE_GIFT_CARDS',
        cookies: [
            'nuu-enable-gift-cards',
        ],
        queryString: 'nuu-enable-gift-cards',
    },
    // NU_FEATURE: REPORT_DAMAGE
    // https://urbnit.atlassian.net/browse/TYP-30347
    reportDamage: {
        default: true,
        multivariate: false,
        splitInfo: {
            splitName: 'customer_reported_damages',
            trafficType: SPLIT_TRAFFIC_TYPES.authenticatedUser,
        },
        envVar: 'NUU_ENABLE_REPORT_DAMAGE',
        cookies: [
            'nuu-enable-report-damage',
        ],
        queryString: 'nuu-enable-report-damage',
    },
    // NU_EXPERIMENT: productSimilarityPdp
    // https://urbnit.atlassian.net/browse/TYP-34099
    productSimilarityPdp: {
        default: '',
        multivariate: true,
        splitInfo: {
            splitName: 'exp_product_similarity_pdp',
            trafficType: SPLIT_TRAFFIC_TYPES.authenticatedUser,
        },
        cookies: [
            'nuu-exp-product-similarity-pdp',
        ],
        queryString: 'nuu-exp-product-similarity-pdp',
    },
    // NU_FEATURE: productSimilarityPageEndpoint
    // https://urbnit.atlassian.net/browse/TYP-34229
    productSimilarityPageEndpoint: {
        default: false,
        multivariate: false,
        splitInfo: {
            splitName: 'ff_product_similarity_page_endpoint',
            trafficType: SPLIT_TRAFFIC_TYPES.authenticatedUser,
        },
        envVar: 'NUU_ENABLE_PRODUCT_SIMILARITY_PAGE_ENDPOINT',
        cookies: [
            'nuu-enable-product-similarity-page-endpoint',
        ],
        queryString: 'nuu-enable-product-similarity-page-endpoint',
    },
    // NU_FEATURE: productSimilarityBox
    // https://urbnit.atlassian.net/browse/TYP-34227
    productSimilarityBox: {
        default: false,
        multivariate: false,
        splitInfo: {
            splitName: 'ff_product_similarity_box',
            trafficType: SPLIT_TRAFFIC_TYPES.allUsers,
        },
        envVar: 'NUU_ENABLE_PRODUCT_SIMILARITY_BOX',
        cookies: [
            'nuu-enable-product-similarity-box',
        ],
        queryString: 'nuu-enable-product-similarity-box',
    },
    // NU_FEATURE: fitImprovements
    // https://urbnit.atlassian.net/browse/TYP-35060
    fitImprovements: {
        default: false,
        multivariate: false,
        splitInfo: {
            splitName: 'ff_fit_improvements',
            trafficType: SPLIT_TRAFFIC_TYPES.allUsers,
        },
        envVar: 'NUU_ENABLE_FIT_IMPROVEMENTS',
        cookies: [
            'nuu-enable-fit-improvements',
        ],
        queryString: 'nuu-enable-fit-improvements',
    },
    // NU_FEATURE: fitGuidance
    // https://urbnit.atlassian.net/browse/TYP-35400
    fitGuidance: {
        default: false,
        multivariate: false,
        splitInfo: {
            splitName: 'ff_fit_guidance',
            trafficType: SPLIT_TRAFFIC_TYPES.allUsers,
        },
        envVar: 'NUU_ENABLE_FIT_GUIDANCE',
        cookies: [
            'nuu-enable-fit-guidance',
        ],
        queryString: 'nuu-enable-fit-guidance',
    },
    // CONTENTFUL FEATURE FLAGS, DO NOT ADD ADD ENV VARS FOR THESE
    useBoxColorSelector: {
        default: false,
        multivariate: false,
        splitInfo: {
            splitName: 'ff_use_box_color_selector',
            trafficType: SPLIT_TRAFFIC_TYPES.allUsers,
        },
        cookies: [
            'use-box-color-selector',
        ],
        queryString: 'use-box-color-selector',
    },
    // NU_FEATURE: recentlyViewedTray
    // https://urbnit.atlassian.net/browse/TYP-35217
    recentlyViewedTray: {
        default: false,
        multivariate: false,
        splitInfo: {
            splitName: 'ff_recently_viewed_tray',
            trafficType: SPLIT_TRAFFIC_TYPES.allUsers,
        },
        cookies: [
            'nuu-enable-recently-viewed-tray',
        ],
        queryString: 'nuu-enable-recently-viewed-tray',
    },
};

// Build the featureConfigs and hard-freeze both the container and each
// individual flag object.  This guarantees the canonical definitions remain
// immutable for the lifetime of the Node process.
const rawFeatureConfigs = getFeatureConfigs(appFeatureConfigs);
const featureConfigs = Object.freeze(
    Object.fromEntries(
        Object.entries(rawFeatureConfigs).map(([key, cfg]) => [key, Object.freeze({ ...cfg })]),
    ),
);

export default featureConfigs;
